import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef } from "react";

import { GqlPlannerAreaGetAutocompleteOptionsParams } from "~/__generated__";

import {
    PlannerAreaSelect_GetAutocompleteOptionsDocument,
    PlannerAreaSelect_GetAutocompleteOptionsQuery,
} from "../queries.generated";

type PlannerAreaOption = PlannerAreaSelect_GetAutocompleteOptionsQuery["plannerAreaGetAutocompleteOptions"]["data"][number];

export default function usePlannerAreaSelect_GetAutocompleteOptions(params: GqlPlannerAreaGetAutocompleteOptionsParams) {
    const knownAreas = useRef<PlannerAreaOption[]>([]);

    const requestedIds = params.selectedIds ?? [];
    const clientIds = params.clientIds ?? [];
    const projectIds = params.projectIds ?? [];

    // Drop `selectedIds` whose client or project is not (or no longer) part of the active filters: the
    // backend rejects areas outside the project scope. Areas we have not seen yet are kept.
    const scrubbedSelectedIds = requestedIds.filter(areaId => {
        const area = knownAreas.current.find(option => option.value === areaId);
        if (area === undefined) return true;
        if (clientIds.length > 0 && !clientIds.includes(area.clientId)) return false;
        if (projectIds.length > 0 && !projectIds.includes(area.projectId)) return false;

        return true;
    });

    // The scrub only ever removes ids, so a length difference means something was dropped.
    const selectedIdsIsScrubbed = scrubbedSelectedIds.length !== requestedIds.length;

    const { data, previousData, loading } = useQuery(PlannerAreaSelect_GetAutocompleteOptionsDocument, {
        variables: { params: { ...params, selectedIds: scrubbedSelectedIds } },
    });

    // Remember the client and project of every area the backend returns so future requests can be scoped.
    useEffect(() => {
        const payload = data?.plannerAreaGetAutocompleteOptions;
        if (!payload) return;

        knownAreas.current = [...payload.data, ...payload.selectedData];
    }, [data]);

    const options = useMemo(() => data?.plannerAreaGetAutocompleteOptions.data ?? [], [data]);

    const selectedOptions = useMemo(
        () => (data ?? previousData)?.plannerAreaGetAutocompleteOptions.selectedData ?? [],
        [data, previousData],
    );

    return { options, selectedOptions, scrubbedSelectedIds, selectedIdsIsScrubbed, loading };
}
